import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Modal, Form, Input, InputNumber, Switch, Popconfirm, Typography } from 'antd';
import { ApiOutlined, PlusOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined, PercentageOutlined, FormOutlined } from '@ant-design/icons';
import AdminPageLayout from './layout/AdminPageLayout';
import usePlatformStore from '../../stores/platformStore';

const Platforms = () => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { platforms, loading, fetchPlatforms, createPlatform, updatePlatform, deletePlatform } = usePlatformStore();

  useEffect(() => {
    fetchPlatforms();
  }, [fetchPlatforms]);

  const showModal = (platform = null) => {
    setEditingPlatform(platform);
    if (platform) {
      form.setFieldsValue(platform);
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || '-',
    },
    {
      title: 'Commission %',
      dataIndex: 'commission_percentage',
      key: 'commission_percentage',
    },
    {
      title: 'CU Required',
      dataIndex: 'cu',
      key: 'cu',
      render: (cu) => cu ? 'Yes' : 'No',
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => showModal(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete Platform"
            description="Are you sure?"
            onConfirm={() => deletePlatform(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <AdminPageLayout
      title="Platforms"
      icon={<ApiOutlined />}
      subtitle="Manage your rental platforms"
      actions={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
          Add Platform
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={platforms}
        loading={loading}
        rowKey="id"
      />

      <Modal
        title={editingPlatform ? "Edit Platform" : "Add Platform"}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingPlatform(null);
        }}
        width={700}
        confirmLoading={submitting}
        okText={submitting ? "Saving..." : "Save"}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            setSubmitting(true);
            try {
              const result = editingPlatform 
                ? await updatePlatform(editingPlatform.id, values)
                : await createPlatform(values);
              
              if (result.success) {
                setIsModalVisible(false);
                form.resetFields();
                setEditingPlatform(null);
              }
            } finally {
              setSubmitting(false);
            }
          }}
          requiredMark="optional"
          validateTrigger="onBlur"
        >
          <Typography.Title level={5} style={{ marginBottom: 24 }}>
            Platform Details
          </Typography.Title>
          
          <Form.Item
            name="name"
            label={
              <Space>
                <FormOutlined />
                <span>Platform Name</span>
              </Space>
            }
            rules={[
              { required: true, message: 'Please input the platform name!' },
              { min: 2, message: 'Name must be at least 2 characters' },
              { max: 50, message: 'Name cannot exceed 50 characters' }
            ]}
            tooltip={{
              title: "The name of the rental platform (e.g., Airbnb, Booking.com)",
              icon: <InfoCircleOutlined />
            }}
          >
            <Input 
              placeholder="Enter platform name"
              allowClear
              prefix={<ApiOutlined className="site-form-item-icon" />}
            />
          </Form.Item>
          
          <Form.Item
            name="description"
            label={
              <Space>
                <InfoCircleOutlined />
                <span>Description</span>
              </Space>
            }
            tooltip={{
              title: "Provide additional details about the platform and its specific requirements",
              icon: <InfoCircleOutlined />
            }}
          >
            <Input.TextArea 
              rows={4} 
              placeholder="Enter platform description (optional)"
              showCount
              maxLength={500}
              style={{ resize: 'none' }}
            />
          </Form.Item>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '20px',
            marginBottom: '24px' 
          }}>
            <Form.Item
              name="commission_percentage"
              label={
                <Space>
                  <PercentageOutlined />
                  <span>Commission Percentage</span>
                </Space>
              }
              rules={[
                { required: true, message: 'Please input the commission percentage!' },
                { type: 'number', min: 0, max: 100, message: 'Percentage must be between 0 and 100' }
              ]}
              tooltip={{
                title: "The percentage of commission charged by the platform for each booking",
                icon: <InfoCircleOutlined />
              }}
            >
              <InputNumber
                min={0}
                max={100}
                formatter={value => `${value}%`}
                parser={value => value?.toString().replace('%', '')}
                style={{ width: '100%' }}
                placeholder="Enter commission percentage"
              />
            </Form.Item>

            <Form.Item
              name="cu"
              label={
                <Space>
                  <ApiOutlined />
                  <span>Certificazione Unica Required</span>
                </Space>
              }
              valuePropName="checked"
              tooltip={{
                title: "Enable if this platform requires Certificazione Unica documentation for tax purposes",
                icon: <InfoCircleOutlined />
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Switch 
                  checkedChildren="Required" 
                  unCheckedChildren="Not Required"
                  style={{ width: 100 }}
                />
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                  Toggle if CU documentation is needed
                </Typography.Text>
              </div>
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </AdminPageLayout>
  );
};

export default Platforms;
